// ── Shared Minecraft bitmap font infrastructure ─────────────────────────
// Used by sign-generator, tellraw-editor, and future tools that render MC text.

// ── Minecraft text colors (JSON text component) ────────────────────────
export const MC_TEXT_COLORS: Record<string, string> = {
  black: '#000000',
  dark_blue: '#0000AA',
  dark_green: '#00AA00',
  dark_aqua: '#00AAAA',
  dark_red: '#AA0000',
  dark_purple: '#AA00AA',
  gold: '#FFAA00',
  gray: '#AAAAAA',
  dark_gray: '#555555',
  blue: '#5555FF',
  green: '#55FF55',
  aqua: '#55FFFF',
  red: '#FF5555',
  light_purple: '#FF55FF',
  yellow: '#FFFF55',
  white: '#FFFFFF',
};

// ── Minecraft bitmap font character widths ──────────────────────────────
// Computed from ascii.png (128x128, 16x16 grid of 8x8 cells)
// Width = rightmost non-transparent column + 2 (Minecraft adds 1px spacing)
export const CHAR_WIDTHS: Record<number, number> = {
  32: 4,  // ' '
  33: 2,  // '!'
  34: 4,  // '"'
  35: 6,  // '#'
  36: 6,  // '$'
  37: 6,  // '%'
  38: 6,  // '&'
  39: 2,  // '''
  40: 4,  // '('
  41: 4,  // ')'
  42: 4,  // '*'
  43: 6,  // '+'
  44: 2,  // ','
  45: 6,  // '-'
  46: 2,  // '.'
  47: 6,  // '/'
  48: 6,  // '0'
  49: 6,  // '1'
  50: 6,  // '2'
  51: 6,  // '3'
  52: 6,  // '4'
  53: 6,  // '5'
  54: 6,  // '6'
  55: 6,  // '7'
  56: 6,  // '8'
  57: 6,  // '9'
  58: 2,  // ':'
  59: 2,  // ';'
  60: 5,  // '<'
  61: 6,  // '='
  62: 5,  // '>'
  63: 6,  // '?'
  64: 7,  // '@'
  65: 6,  // 'A'
  66: 6,  // 'B'
  67: 6,  // 'C'
  68: 6,  // 'D'
  69: 6,  // 'E'
  70: 6,  // 'F'
  71: 6,  // 'G'
  72: 6,  // 'H'
  73: 4,  // 'I'
  74: 6,  // 'J'
  75: 6,  // 'K'
  76: 6,  // 'L'
  77: 6,  // 'M'
  78: 6,  // 'N'
  79: 6,  // 'O'
  80: 6,  // 'P'
  81: 6,  // 'Q'
  82: 6,  // 'R'
  83: 6,  // 'S'
  84: 6,  // 'T'
  85: 6,  // 'U'
  86: 6,  // 'V'
  87: 6,  // 'W'
  88: 6,  // 'X'
  89: 6,  // 'Y'
  90: 6,  // 'Z'
  91: 4,  // '['
  92: 6,  // '\\'
  93: 4,  // ']'
  94: 6,  // '^'
  95: 6,  // '_'
  96: 3,  // '`'
  97: 6,  // 'a'
  98: 6,  // 'b'
  99: 6,  // 'c'
  100: 6, // 'd'
  101: 6, // 'e'
  102: 5, // 'f'
  103: 6, // 'g'
  104: 6, // 'h'
  105: 2, // 'i'
  106: 6, // 'j'
  107: 5, // 'k'
  108: 3, // 'l'
  109: 6, // 'm'
  110: 6, // 'n'
  111: 6, // 'o'
  112: 6, // 'p'
  113: 6, // 'q'
  114: 6, // 'r'
  115: 6, // 's'
  116: 4, // 't'
  117: 6, // 'u'
  118: 6, // 'v'
  119: 6, // 'w'
  120: 6, // 'x'
  121: 6, // 'y'
  122: 6, // 'z'
  123: 4, // '{'
  124: 2, // '|'
  125: 4, // '}'
  126: 7, // '~'
};

export function getCharWidth(charCode: number, bold: boolean): number {
  const base = CHAR_WIDTHS[charCode] ?? 6;
  return bold ? base + 1 : base;
}

// ── Font texture constants ──────────────────────────────────────────────
export const FONT_TEXTURE_PATH = '/minecraft-assets/textures/font/ascii.png';
export const FONT_GRID_SIZE = 16;   // 16x16 grid
export const FONT_CELL_SIZE = 8;    // 8x8 pixels per cell
export const FONT_TEXTURE_SIZE = 128; // 128x128 total

// ── Font texture loading ────────────────────────────────────────────────

let fontImageData: ImageData | null = null;
let fontLoadPromise: Promise<ImageData> | null = null;

export function loadFontTexture(): Promise<ImageData> {
  if (fontImageData) return Promise.resolve(fontImageData);
  if (fontLoadPromise) return fontLoadPromise;

  fontLoadPromise = new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      fontImageData = ctx.getImageData(0, 0, img.width, img.height);
      resolve(fontImageData);
    };
    img.onerror = reject;
    img.src = FONT_TEXTURE_PATH;
  });

  return fontLoadPromise;
}

// Generalised atlas loader (any font PNG → ImageData), cached per src. Used for the
// alternate bitmap fonts (Standard Galactic `ascii_sga.png`, Illager `asciillager.png`),
// which share the ascii layout so `drawGlyph` renders them directly.
const atlasCache = new Map<string, ImageData>();
const atlasPromises = new Map<string, Promise<ImageData>>();
export function loadFontImageData(src: string): Promise<ImageData> {
  const cached = atlasCache.get(src);
  if (cached) return Promise.resolve(cached);
  const pending = atlasPromises.get(src);
  if (pending) return pending;
  const p = new Promise<ImageData>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, img.width, img.height);
      atlasCache.set(src, data);
      resolve(data);
    };
    img.onerror = (e) => {
      // Don't leave a rejected promise cached, or every later call returns it and
      // the font is permanently broken until a full reload. Allow a retry.
      atlasPromises.delete(src);
      reject(e);
    };
    img.src = src;
  });
  atlasPromises.set(src, p);
  return p;
}

// ── Color helpers ───────────────────────────────────────────────────────

export function hexToRGB(hex: string): [number, number, number] {
  const h = hex.startsWith('#') ? hex.slice(1) : hex;
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

export function resolveTextColor(color: string | undefined, defaultHex: string): [number, number, number] {
  if (!color) return hexToRGB(defaultHex);
  if (color.startsWith('#')) return hexToRGB(color);
  const hex = MC_TEXT_COLORS[color];
  return hex ? hexToRGB(hex) : hexToRGB(defaultHex);
}

/** Pack an `#rrggbb` hex into a signed 32-bit ARGB int (alpha 0xFF) — the form the
 * `shadow_color` text-component field takes (e.g. white → -1). */
export function packArgbFromHex(hex: string): number {
  const [r, g, b] = hexToRGB(hex);
  return (0xff000000 | (r << 16) | (g << 8) | b) | 0;
}

/** Inverse of packArgbFromHex — drops alpha, returns `#rrggbb`. */
export function argbToHex(argb: number): string {
  const r = (argb >> 16) & 0xff;
  const g = (argb >> 8) & 0xff;
  const b = argb & 0xff;
  return `#${[r, g, b].map(n => n.toString(16).padStart(2, '0')).join('')}`;
}

// ── Glyph drawing ───────────────────────────────────────────────────────

export function drawGlyph(
  ctx: CanvasRenderingContext2D,
  font: ImageData,
  charCode: number,
  x: number,
  y: number,
  scale: number,
  r: number,
  g: number,
  b: number,
) {
  if (charCode < 32 || charCode > 126) return;

  const col = charCode % FONT_GRID_SIZE;
  const row = Math.floor(charCode / FONT_GRID_SIZE);
  const srcX = col * FONT_CELL_SIZE;
  const srcY = row * FONT_CELL_SIZE;

  // The cell may fall outside atlases shorter than 128px (e.g. the 128x40
  // asciillager.png). Reading past the buffer yields `undefined`, and
  // `undefined < 128` is false — which would DRAW a solid block. Skip instead.
  if (srcX + FONT_CELL_SIZE > font.width || srcY + FONT_CELL_SIZE > font.height) return;

  for (let py = 0; py < FONT_CELL_SIZE; py++) {
    for (let px = 0; px < FONT_CELL_SIZE; px++) {
      const idx = ((srcY + py) * font.width + (srcX + px)) * 4;
      const alpha = font.data[idx + 3];
      if (!(alpha >= 128)) continue; // also skips a stray undefined

      ctx.fillStyle = `rgb(${r},${g},${b})`;
      ctx.fillRect(
        Math.floor(x + px * scale),
        Math.floor(y + py * scale),
        Math.ceil(scale),
        Math.ceil(scale),
      );
    }
  }
}
